export type MarketplaceState = "pre" | "post";

type BiState<T extends string = string> = { pre: T; post: T };

export const VERIFIED_COPY = {
  strip: {
    subline: {
      pre: "Verify now so your Buyer Passport is ready for future agent marketplace offers.",
      post: "Verified buyers can access stronger incentive offers on the Sill marketplace.",
    } satisfies BiState,
  },
  explainer: {
    tier3Unlock: {
      pre: "A stronger profile can help agents understand your numbers when the marketplace opens.",
      post: "A bank-backed profile helps agents understand which numbers come from connected sources.",
    } satisfies BiState,
    tier4Unlock: {
      pre: "A lender has reviewed your file and pre-approved your financing.",
      post: "A lender has reviewed your file and pre-approved your financing.",
    } satisfies BiState,
    footer: {
      pre: "The self-serve agent marketplace is launching market by market.",
      post: "The Sill marketplace is live. Higher tiers unlock stronger offers today.",
    } satisfies BiState,
  },
  journeyRail: {
    step5Subtitle: {
      pre: "Verified profile - ready for agent marketplace offers when your market opens.",
      post: "Verified profile - receiving marketplace incentive offers now.",
    } satisfies BiState,
  },
  advisorChip: {
    tierNextStep: {
      pre: "Verify now so your Buyer Passport reflects better-supported numbers.",
      post: "Verify now to unlock stronger marketplace incentive offers today.",
    } satisfies BiState,
  },
} as const;

type FlattenedCopy = {
  [G in keyof typeof VERIFIED_COPY]: {
    [K in keyof (typeof VERIFIED_COPY)[G]]: string;
  };
};

export function getVerifiedCopy(state: MarketplaceState): FlattenedCopy {
  const out = {} as Record<string, Record<string, string>>;
  for (const [group, keys] of Object.entries(VERIFIED_COPY)) {
    const flat: Record<string, string> = {};
    for (const [key, states] of Object.entries(keys)) {
      flat[key] = (states as BiState)[state];
    }
    out[group] = flat;
  }
  return out as FlattenedCopy;
}

function collectPhrases(): string[] {
  const phrases = new Set<string>();
  for (const groupKeys of Object.values(VERIFIED_COPY)) {
    for (const states of Object.values(groupKeys)) {
      const bi = states as BiState;
      phrases.add(bi.pre);
      phrases.add(bi.post);
    }
  }
  return [...phrases];
}

export const MARKETPLACE_COPY_GUARDED_PHRASES: readonly string[] =
  collectPhrases();
